import { Info } from "lucide-react";
import React from "react";
import { HappeningNowPlatformChips } from "src/components/HappeningNow/HappeningNowPlatformChips";
import { DateTimePicker } from "src/components/ui/DateTimePicker";
import type { ComposerDefaults } from "src/lib/happeningNowComposerDefaults";

const labelClass =
  "flex flex-col gap-2 py-3 text-sm font-extrabold uppercase tracking-wide text-white/70";

interface ComposerDefaultsBarProps {
  defaults: ComposerDefaults;
  onPatch: (patch: Partial<ComposerDefaults>) => void;
}

/**
 * Shared defaults for the composer. Acts as a master control: changing a value
 * here cascades to every card (handled in the store) and is persisted to
 * localStorage for next time.
 */
export const ComposerDefaultsBar: React.FC<ComposerDefaultsBarProps> = ({
  defaults,
  onPatch,
}) => {
  return (
    <section
      className="rounded-[1.75rem] border-4 border-white/10 bg-white/[0.04] p-4 shadow-[0_6px_0_rgba(0,0,0,0.3)] backdrop-blur-sm sm:p-5"
      aria-labelledby="composer-defaults-heading"
    >
      <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1">
        <h2
          id="composer-defaults-heading"
          className="text-base font-extrabold text-white"
        >
          Defaults for new cards
        </h2>
        <span className="inline-flex items-center gap-1 text-xs font-bold text-white/50">
          <Info className="h-3.5 w-3.5" aria-hidden />
          applies to every card · saved for next time
        </span>
      </div>

      <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:gap-8">
        <HappeningNowPlatformChips
          tone="dark"
          groupLabel="Platforms"
          androidSelected={defaults.platformAndroid}
          iosSelected={defaults.platformIos}
          onToggleAndroid={() => {
            const next = !defaults.platformAndroid;
            if (!next && !defaults.platformIos) return;
            onPatch({ platformAndroid: next });
          }}
          onToggleIos={() => {
            const next = !defaults.platformIos;
            if (!next && !defaults.platformAndroid) return;
            onPatch({ platformIos: next });
          }}
        />

        <label className={`${labelClass} sm:w-64`}>
          Expires
          <DateTimePicker
            className="cursor-pointer"
            accent="teal"
            value={defaults.expiresAtLocal}
            onChanged={({ value }) => onPatch({ expiresAtLocal: value })}
            required
            aria-label="Default card expiry date and time"
          />
        </label>
      </div>
    </section>
  );
};
